import { format, addMinutes, startOfDay, setHours, setMinutes } from "date-fns";
import { toZonedTime, fromZonedTime } from "date-fns-tz";

export interface TimeSlot {
  value: string; // "HH:mm" format
  label: string; // "h:mm a" format (12-hour)
}

/**
 * Generate array of 30-minute interval time slots for the entire day
 */
export function generateTimeSlots(): TimeSlot[] {
  const slots: TimeSlot[] = [];
  let current = startOfDay(new Date());
  
  for (let i = 0; i < 48; i++) { // 24 hours * 2 (30-minute intervals)
    const value = format(current, "HH:mm");
    const label = format(current, "h:mm a");
    slots.push({ value, label });
    current = addMinutes(current, 30);
  }
  
  return slots;
}

/**
 * Round a time to the nearest 30-minute interval
 */
export function roundToNearest30Minutes(date: Date): Date {
  const minutes = date.getMinutes();
  const roundedMinutes = minutes < 30 ? 30 : 0;
  const hoursAdjustment = minutes >= 30 && minutes < 60 ? 1 : 0;
  
  let result = setMinutes(date, roundedMinutes);
  if (hoursAdjustment > 0) {
    result = setHours(result, date.getHours() + hoursAdjustment);
  }
  result.setSeconds(0);
  result.setMilliseconds(0);
  
  return result;
}

/**
 * Convert a date and time string ("HH:mm") to a Date object
 */
export function combineDateAndTime(date: Date, timeString: string): Date {
  const [hours, minutes] = timeString.split(":").map(Number);
  let result = new Date(date);
  result = setHours(result, hours);
  result = setMinutes(result, minutes);
  result.setSeconds(0);
  result.setMilliseconds(0);
  return result;
}

/**
 * Convert local datetime to UTC for storage
 */
export function localToUTC(localDate: Date, timezone: string): Date {
  return fromZonedTime(localDate, timezone);
}

/**
 * Convert UTC datetime to local timezone for display
 */
export function utcToLocal(utcDate: Date, timezone: string): Date {
  return toZonedTime(utcDate, timezone);
}

/**
 * Format a date for display with timezone consideration
 */
export function formatScheduledTime(date: Date, timezone: string): string {
  const localDate = utcToLocal(date, timezone);
  return format(localDate, "PPp"); // e.g., "Apr 29, 2023, 9:30 AM"
}

/**
 * Get user's current timezone
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Common timezone options for the UI
 */
export const COMMON_TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Paris (CET/CEST)" },
  { value: "Europe/Berlin", label: "Berlin (CET/CEST)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Shanghai", label: "China (CST)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Singapore", label: "Singapore (SGT)" },
  { value: "Australia/Sydney", label: "Sydney (AEDT/AEST)" },
  { value: "Pacific/Auckland", label: "Auckland (NZDT/NZST)" },
  { value: "UTC", label: "UTC" },
];
